import { tryOnScopeDispose, until } from "@vueuse/core";

export const useLocation = () => {
  const navigator = isClient() ? window.navigator : undefined;
  const isGeolocationSupported = ref(navigator && "geolocation" in navigator);
  const locationPermissionStatus = useSafeLocalStorage("location-status", "unknown");
  const isLocationSupported = computed(
    () => !isClient() || (isGeolocationSupported.value && locationPermissionStatus.value !== "denied")
  );
  const loading = ref(!isClient());
  const coords = ref({});
  const error = ref(null);
  const locatedAt = ref(null);
  const watcher = ref(null);
  const geolocationSettings = {
    enableHighAccuracy: true,
    maximumAge: 30000,
    timeout: 5000,
  };
  // web lookup is more accurate than most device altimeters, so use it by default
  const useWeb = ref(true);
  // result of the web lookup for one position: { key, status: "pending" | "ok" | "none" | "failed", altitude }
  const webLookup = ref(null);
  const coordsKey = computed(() =>
    isNumber(coords.value?.latitude) ? `${coords.value.latitude},${coords.value.longitude}` : null,
  );

  async function updatePosition(position) {
    locatedAt.value = position.timestamp;
    coords.value = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      altitude: position.coords.altitude,
      accuracy: position.coords.accuracy,
      altitudeAccuracy: position.coords.altitudeAccuracy,
      heading: position.coords.heading,
      speed: position.coords.speed,
    };
    error.value = null;
    locationPermissionStatus.value = "allowed";
    if (!isNumber(position.coords.altitude)) {
      useWeb.value = true;
    }
    if (!useWeb.value) {
      loading.value = false;
    }
  }

  function handleError(err) {
    error.value = err;
    loading.value = false;
    if (err.code === 1) {
      locationPermissionStatus.value = "denied";
    }
  }

  async function updateLocation() {
    if (!isGeolocationSupported.value) {
      return;
    }
    loading.value = true;
    locationPermissionStatus.value = "unknown";
    if (webLookup.value?.status === "failed") {
      // allow a retry of a failed lookup
      webLookup.value = null;
    }
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          updatePosition(position);
          resolve();
        },
        (err) => {
          handleError(err);
          resolve();
        },
        geolocationSettings
      );
    });
  }

  function startWatching() {
    if (!isGeolocationSupported.value) {
      return;
    }
    stopWatching();
    loading.value = true;
    locationPermissionStatus.value = "unknown";
    watcher.value = navigator.geolocation.watchPosition(updatePosition, handleError, geolocationSettings);
  }

  function stopWatching() {
    if (watcher.value !== null && navigator) {
      navigator.geolocation.clearWatch(watcher.value);
    }
    watcher.value = null;
  }

  tryOnScopeDispose(stopWatching);

  const isWatching = computed(() => {
    return watcher.value !== null;
  });

  // "none" means the lookup succeeded but there is no elevation data for the point (e.g. open ocean);
  // anything that goes wrong is "failed"
  async function lookUpWebElevation(key, position) {
    webLookup.value = { key, status: "pending" };
    let result = { status: "failed" };
    try {
      const { data, error: fetchError, pending } = await useFetchElevation(position);
      if (pending?.value) {
        await until(pending).toBe(false);
      }
      result = { status: elevationLookupStatus(data.value, fetchError?.value), altitude: data.value };
    } catch (err) {
      error.value = err;
    }
    if (webLookup.value?.key === key) {
      webLookup.value = { key, ...result };
      loading.value = false;
    }
  }

  watch([coords, useWeb], () => {
    const key = coordsKey.value;
    if (!useWeb.value || !key) {
      return;
    }
    if (webLookup.value?.key === key) {
      // already looked up (or in flight) for this position
      if (webLookup.value.status !== "pending") {
        loading.value = false;
      }
      return;
    }
    return lookUpWebElevation(key, coords.value);
  });

  const currentWebLookup = computed(() => (webLookup.value?.key === coordsKey.value ? webLookup.value : null));

  // set when there is no elevation to show because the web lookup found nothing or failed
  const elevationStatus = computed(() => {
    const status = currentWebLookup.value?.status;
    if (!useWeb.value || (status !== "none" && status !== "failed")) {
      return null;
    }
    return status;
  });

  const location = computed(() => {
    if (useWeb.value) {
      return {
        ...coords.value,
        altitude: currentWebLookup.value?.altitude,
        altitudeAccuracy: null,
        altitudeSource: "web",
      };
    }
    return coords.value;
  });

  return {
    updateLocation,
    startWatching,
    stopWatching,
    isWatching,
    useWeb,
    locationPermissionStatus,
    isLocationSupported,
    location,
    locatedAt,
    error,
    loading,
    elevationStatus,
  };
};
